import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
    InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Search, LibraryBooks } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface Plan {
    id: number;
    name: string;
    price: number;
    max_citizens: number;
    max_users: number;
    max_storage: number;
    duration_days: number;
}

interface PlanFormData {
    name: string;
    price: number;
    max_citizens: number;
    max_users: number;
    max_storage: number;
    duration_days: number;
}

const initialFormData: PlanFormData = {
    name: '',
    price: 0,
    max_citizens: 1000,
    max_users: 5,
    max_storage: 100,
    duration_days: 30,
};

const Plans = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [formData, setFormData] = useState<PlanFormData>(initialFormData);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<Plan | null>(null);

    // Fetch plans
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['plans', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/plans', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data: PlanFormData) => {
            if (selectedPlan) {
                return api.put(`/superadmin/plans/${selectedPlan.id}`, data);
            }
            return api.post('/superadmin/plans', data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['plans'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to save plan');
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/plans/${id}`),
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['plans'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message || 'Failed to delete plan');
        },
    });

    const handleOpen = (plan?: Plan) => {
        if (plan) {
            setSelectedPlan(plan);
            setFormData({
                name: plan.name,
                price: plan.price,
                max_citizens: plan.max_citizens,
                max_users: plan.max_users,
                max_storage: plan.max_storage,
                duration_days: plan.duration_days,
            });
        } else {
            setSelectedPlan(null);
            setFormData(initialFormData);
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedPlan(null);
        setFormData(initialFormData);
        setError('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (plan: Plan) => {
        setPlanToDelete(plan);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (planToDelete) {
            deleteMutation.mutate(planToDelete.id);
        }
        setDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setPlanToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },

        { field: 'name', headerName: 'Plan Types', flex: 1, minWidth: 120 },
        { field: 'price', headerName: 'Price (₹)', width: 100 },
        { field: 'duration_days', headerName: 'Days', width: 80 },
        { field: 'max_citizens', headerName: 'Citizens', width: 100 },
        { field: 'max_users', headerName: 'Users', width: 80 },
        { field: 'max_storage', headerName: 'Storage (MB)', width: 110 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, mb: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, mb: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns (fewer)
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'price', headerName: 'Price', width: 80 },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    console.log("Plans data:", rows);
    const totalRows = data?.data?.total || data?.total || 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">Subscription Plans</Typography>
            </Box>

            {/* Search */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', borderRadius: 1 }}
                        size="small"
                        placeholder="Search plans..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button variant="contained" startIcon={<LibraryBooks />} onClick={() => handleOpen()}>
                        {isMobile ? 'Add' : 'Add Plan'}
                    </Button>
                </CardContent>
            </Card>

            {error && !open && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                    <DataGrid
                        rows={rows}
                        columns={isMobile ? mobileColumns : columns}
                        loading={isLoading}
                        rowCount={totalRows}
                        paginationModel={pageModel}
                        onPaginationModelChange={setPageModel}
                        paginationMode="server"
                        pageSizeOptions={[10, 25, 50]}
                        disableRowSelectionOnClick
                        autoHeight
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-cell': { py: 1 },
                        }}
                    />
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>{selectedPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <TextField
                            label="Plan Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <TextField
                                label="Price (₹)"
                                type="number"
                                fullWidth
                                required
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                            />
                            <TextField
                                label="Duration (Days)"
                                type="number"
                                fullWidth
                                required
                                value={formData.duration_days}
                                onChange={(e) => setFormData({ ...formData, duration_days: Number(e.target.value) })}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <TextField
                                label="Max Citizens"
                                type="number"
                                fullWidth
                                required
                                value={formData.max_citizens}
                                onChange={(e) => setFormData({ ...formData, max_citizens: Number(e.target.value) })}
                            />
                            <TextField
                                label="Max Users"
                                type="number"
                                fullWidth
                                required
                                value={formData.max_users}
                                onChange={(e) => setFormData({ ...formData, max_users: Number(e.target.value) })}
                            />
                        </Box>
                        <TextField
                            label="Max Storage (MB)"
                            type="number"
                            fullWidth
                            required
                            value={formData.max_storage}
                            onChange={(e) => setFormData({ ...formData, max_storage: Number(e.target.value) })}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    bgcolor: '#fee2e2',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: '1px solid #fecaca'
                }}>
                    <Box sx={{
                        bgcolor: '#dc2626',
                        borderRadius: '50%',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                            Confirm Delete
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>
                            This action cannot be undone
                        </Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{planToDelete?.name}"</strong> plan?
                    </Typography>
                    <Alert severity="warning" sx={{
                        bgcolor: '#fef3c7',
                        border: '1px solid #fcd34d',
                        '& .MuiAlert-icon': { color: '#d97706' }
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: You will not be able to revert this action!
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            Tenants currently using this plan may be affected.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button
                        variant="outlined"
                        onClick={handleDeleteCancel}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleteMutation.isPending}
                        startIcon={<Delete />}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#dc2626',
                            '&:hover': { bgcolor: '#b91c1c' }
                        }}
                    >
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete Plan'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Plans;
